import type { ReactElement } from "react";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { MenuOption } from "@smartsuite/react-ui/lib/components/dropdowns/MenuOption";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { ifLoopingSourceIsActionFind } from "../../fields/utils";
import { resolveCurrentItemScope } from "../../../../utils/action-sources";
import type { DynamicSection } from "../../../../../../../../types/line";
import type { LoopingOptionGroup } from "../../../../MemberEditor/utils";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";

export const CurrentOption = observer(
  ({
    addPill,
    loopingMenu,
    loopScope: explicitScope,
    actionsArePreScoped,
  }: {
    /** Receives the scope this option was rendered for -- the component only
     * renders once it has a valid one, so handlers need no undefined check. */
    addPill: (loopScope: LoopScope) => void;
    loopingMenu?: DynamicSection | LoopingOptionGroup | null;
    /** The loop this editor belongs to, when the caller knows it better than
     * the store does. Conditional Logic branch conditions pass their block's
     * enclosing loop, since no action panel is open for them (BUG-21770). */
    loopScope?: LoopScope;
    /** Marks `loopScope` as authoritative -- `undefined` then means "this
     * branch is not inside a loop", not "ask the store". */
    actionsArePreScoped?: boolean;
  }): ReactElement | null => {
    const store = useRootStore();

    const loopScope = resolveCurrentItemScope(
      store.automations.candidate,
      explicitScope,
      actionsArePreScoped,
    );

    if (loopScope === undefined) {
      return null;
    }

    const hasArrow =
      Boolean(loopingMenu?.options?.length) ||
      ifLoopingSourceIsActionFind(loopScope);

    return (
      <MenuOption
        submenuArrow={hasArrow ? "right" : undefined}
        onClick={() => addPill(loopScope)}
      >
        {t("general.current-item")}
      </MenuOption>
    );
  },
);
